"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Selection, SortDescriptor } from "react-aria-components";
import type { SortingState, PaginationState } from "@hs/ui";
import {
  AlertDialog,
  Button,
  cn,
  Dropdown,
  Label,
  ListBox,
  Pagination,
  SearchField,
  Separator,
  toast,
} from "@heroui/react";
import { DataGrid, InlineSelect } from "@heroui-pro/react";
import type { DataGridColumn } from "@heroui-pro/react";
import {
  IconUpload as Upload,
  IconRefresh as RefreshCw,
  IconTrash as Trash2,
  IconDotsVertical as MoreVertical,
  IconDownload as Download,
  IconEye as Eye,
} from "@tabler/icons-react";
import { trpc } from "@/lib/trpc";
import { apiClient } from "@hs/shared/utils/api-client";
import type { AssetsScope, MediaTypeFilter } from "./AssetsSidebar";
import { formatRelativeTime } from "@hs/shared/utils/formatTime";
import { UploadFilesModal } from "@/components/custom";
import { AssetsPreviewDialog, type PreviewMedia } from "./AssetsPreviewDialog";
import { useAppSelector } from "@/stores/hooks";
import { usePermission } from "@hs/shared/hooks/usePermission";
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const ROW_ACTIONS_ID = "__actions__";
const DEFAULT_PAGE_SIZE = 20;

const MEDIA_TYPE_LABELS: Record<string, string> = {
  image: "图片",
  document: "文档",
  audio: "音频",
  video: "视频",
  archive: "压缩包",
  data: "数据",
  binary: "二进制",
  canvas: "画布",
  block: "块",
};

function formatFileSize(bytes?: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function sortingToDescriptor(sorting: SortingState): SortDescriptor | undefined {
  const s = sorting[0];
  if (!s) return undefined;
  return { column: s.id, direction: s.desc ? "descending" : "ascending" };
}

function descriptorToSorting(descriptor: SortDescriptor | undefined): SortingState {
  if (!descriptor?.column) return [];
  return [{ id: String(descriptor.column), desc: descriptor.direction === "descending" }];
}

function buildPaginationPages(totalPages: number, safePage: number): (number | "ellipsis")[] {
  const pages: (number | "ellipsis")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push("ellipsis");
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
  }
  return pages;
}

export interface AssetsListProps {
  scope: AssetsScope;
  mediaType: MediaTypeFilter;
  selectedCategoryId: string | null;
  scopeLabel: string;
  typeLabel: string;
  selectedCategoryName: string | null;
}

type MediaItem = {
  id: string;
  name?: string | null;
  type?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  url?: string | null;
};

export function AssetsList({
  scope,
  mediaType,
  selectedCategoryId,
  scopeLabel,
  typeLabel,
  selectedCategoryName,
}: AssetsListProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());

  const [isBatchDeleteDialogOpen, setIsBatchDeleteDialogOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<PreviewMedia | null>(null);

  const user = useAppSelector((state) => state.auth.user);
  const { hasPermission: hasSharedManagePermission } = usePermission(user, {
    roles: ["TENANT.OWNER"],
  });
  const canManageShared = scope === "shared" ? hasSharedManagePermission : true;
  const canManageFiles = scope === "my" || canManageShared;

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setSelectedKeys(new Set());
  }, [scope, mediaType, selectedCategoryId]);

  const sortBy = useMemo(() => (sorting[0]?.id ?? undefined), [sorting]);
  const sortOrder = useMemo(
    () => (sorting[0] ? (sorting[0].desc ? "desc" : "asc") : undefined),
    [sorting]
  );

  const typeFilter =
    mediaType === "all" ? undefined : (mediaType as "document" | "image" | "audio" | "video");

  const listMyQuery = trpc.assets.listMy.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sortBy && ["type", "name", "fileSize"].includes(sortBy)
          ? (sortBy as "type" | "name" | "fileSize")
          : undefined,
      sortOrder,
      type: typeFilter ?? undefined,
      where: searchKeyword.trim()
        ? ({ name: { $like: `%${searchKeyword.trim()}%` } } as Record<string, unknown>)
        : undefined,
    } as never,
    { enabled: scope === "my" && selectedCategoryId == null }
  );

  const listSharedQuery = trpc.assets.listSpaceAssets.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sortBy && ["type", "name", "fileSize"].includes(sortBy)
          ? (sortBy as "type" | "name" | "fileSize")
          : undefined,
      sortOrder,
      type: typeFilter ?? undefined,
      ...(searchKeyword.trim()
        ? { where: { name: { $like: `%${searchKeyword.trim()}%` } } as Record<string, unknown> }
        : {}),
    } as never,
    { enabled: scope === "shared" && selectedCategoryId == null }
  );

  const listByCategoryQuery = trpc.assets.listByCategory.useQuery(
    {
      categoryId: selectedCategoryId!,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sortBy && ["createdAt", "name", "fileSize"].includes(sortBy)
          ? (sortBy as "createdAt" | "name" | "fileSize")
          : undefined,
      sortOrder,
    } as never,
    { enabled: selectedCategoryId != null }
  );

  const { data, isLoading, refetch } =
    selectedCategoryId != null
      ? listByCategoryQuery
      : scope === "my"
        ? listMyQuery
        : listSharedQuery;

  const items = (data?.items ?? []) as MediaItem[];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / (pagination.pageSize || DEFAULT_PAGE_SIZE)));

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = useCallback(async (media: MediaItem) => {
    const id = media.id;
    if (!id) return;
    setDownloadingId(id);
    try {
      await apiClient.downloadFile(`assets/${id}/download`, undefined, media.name ?? undefined);
      toast.success("下载已开始");
    } catch (err) {
      toast.danger(err instanceof Error ? err.message : "下载失败");
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const renderActions = useCallback(
    (media: MediaItem) => {
      const mime = media.mimeType ?? "";
      const hasUrl = !!media.url;
      const cosDocPreviewMimes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/plain",
        "text/csv",
      ];
      const canPreview = hasUrl && cosDocPreviewMimes.includes(mime);
      const isDownloading = downloadingId === media.id;

      const handlePreviewClick = () => {
        if (!media.id || !media.url) return;
        setPreviewMedia({
          id: media.id,
          name: media.name ?? "",
          mimeType: mime,
          url: media.url,
        });
        setPreviewOpen(true);
      };

      const menuItems: {
        id: string;
        label: string;
        onPress: () => void;
      }[] = [];
      if (canPreview) {
        menuItems.push({ id: "preview", label: "预览", onPress: handlePreviewClick });
      }
      menuItems.push({
        id: "download",
        label: isDownloading ? "下载中…" : "下载",
        onPress: () => void handleDownload(media),
      });

      return (
        <div className="flex justify-center">
          <Dropdown>
            <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
              <MoreVertical className="size-4" stroke={1.5} />
            </Button>
            <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
              <Dropdown.Menu>
                {menuItems.map((it) => (
                  <Dropdown.Item
                    key={it.id}
                    id={it.id}
                    textValue={it.label}
                    onAction={it.onPress}
                    isDisabled={it.id === "download" && isDownloading}
                  >
                    <Label className="flex items-center gap-2">
                      {it.id === "preview" ? <Eye className="size-4" stroke={1.5} /> : null}
                      {it.id === "download" ? <Download className="size-4" stroke={1.5} /> : null}
                      {it.label}
                    </Label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </div>
      );
    },
    [downloadingId, handleDownload]
  );

  const dataColumns = useMemo<DataGridColumn<MediaItem>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "名称",
        minWidth: 280,
        allowsSorting: true,
        allowsResizing: true,
        isRowHeader: true,
        cell: (row) => <span className="text-sm font-medium">{row.name ?? "—"}</span>,
      },
      {
        id: "type",
        accessorKey: "type",
        header: "类型",
        minWidth: 100,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">
            {MEDIA_TYPE_LABELS[row.type ?? ""] ?? row.type ?? "—"}
          </span>
        ),
      },
      {
        id: "fileSize",
        accessorKey: "fileSize",
        header: "大小",
        minWidth: 100,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">{formatFileSize(row.fileSize)}</span>
        ),
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: "创建时间",
        minWidth: 140,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">
            {row.createdAt ? formatRelativeTime(row.createdAt) : "—"}
          </span>
        ),
      },
    ],
    []
  );

  const actionsColumn = useMemo<DataGridColumn<MediaItem>>(
    () => ({
      id: ROW_ACTIONS_ID,
      header: "",
      align: "end",
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      allowsSorting: false,
      allowsResizing: false,
      pinned: "end",
      cell: (item) => (
        <div className="flex min-w-0 justify-end gap-1 overflow-hidden">{renderActions(item)}</div>
      ),
    }),
    [renderActions]
  );

  const columns = useMemo(() => [...dataColumns, actionsColumn], [dataColumns, actionsColumn]);

  const sortableColumnIds = useMemo(() => {
    if (selectedCategoryId != null) return new Set(["createdAt", "name", "fileSize"]);
    return new Set(["type", "name", "fileSize"]);
  }, [selectedCategoryId]);

  const columnsWithRowHeader = useMemo(() => {
    const first = columns.find((c) => c.id !== ROW_ACTIONS_ID);
    const fh = first?.id;
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
      allowsSorting: sortableColumnIds.has(String(c.id)) ? true : (c.allowsSorting ?? false),
    }));
  }, [columns, sortableColumnIds]);

  const sortDescriptor = useMemo(() => sortingToDescriptor(sorting), [sorting]);

  const handleSortChange = useCallback((d: SortDescriptor) => {
    setSorting(descriptorToSorting(d));
  }, []);

  const handleRefresh = useCallback(() => void refetch(), [refetch]);

  const deleteMediaMutation = trpc.assets.delete.useMutation({
    onSuccess: () => {
      void refetch();
    },
  });

  const selectedCount =
    selectedKeys === "all" ? items.length : selectedKeys instanceof Set ? selectedKeys.size : 0;

  const handleBatchDeleteClick = useCallback(() => {
    if (selectedCount === 0) {
      toast.warning("请先选择要删除的文件");
      return;
    }
    setIsBatchDeleteDialogOpen(true);
  }, [selectedCount]);

  const handleConfirmBatchDelete = useCallback(async () => {
    let ids: string[] = [];
    if (selectedKeys === "all") {
      ids = items.map((p) => p.id).filter(Boolean) as string[];
    } else if (selectedKeys instanceof Set) {
      ids = [...selectedKeys].map(String);
    }
    if (ids.length === 0) return;
    for (const id of ids) {
      await deleteMediaMutation.mutateAsync({ id });
    }
    toast.success(`已删除 ${ids.length} 个文件`);
    setIsBatchDeleteDialogOpen(false);
    setSelectedKeys(new Set());
    void refetch();
  }, [selectedKeys, items, deleteMediaMutation, refetch]);

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = useMemo(() => buildPaginationPages(pageCount, safePage), [pageCount, safePage]);
  const pageSizes = [10, 20, 50, 100];

  const getRowId = useCallback((row: MediaItem) => String(row.id ?? ""), []);

  const headerTitle =
    selectedCategoryId != null && selectedCategoryName
      ? `${scopeLabel} · ${selectedCategoryName}`
      : `${scopeLabel} · ${typeLabel}`;

  return (
    <>
      <div className="flex h-14 shrink-0 items-center border-b border-[var(--border)] px-4">
        <h1 className="text-2xl font-bold">{headerTitle}</h1>
      </div>
      <div className={cn("bg-background flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4")}>
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              "grid min-h-0 min-w-0 w-full flex-1 gap-y-3",
              "grid-rows-[auto_minmax(0,1fr)_auto]"
            )}
          >
            <div className="relative z-10 flex min-w-0 shrink-0 flex-col gap-3">
              <div className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <SearchField
                    aria-label="搜索文件"
                    value={searchKeyword}
                    onChange={(v) => {
                      setSearchKeyword(v);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                  >
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input
                        className="w-[min(100%,220px)] min-w-[160px]"
                        placeholder="搜索名称"
                      />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <Button variant="secondary" onPress={handleRefresh}>
                    <RefreshCw className="size-4" stroke={1.5} />
                    刷新
                  </Button>
                  {canManageFiles ? (
                    <>
                      <Button
                        variant="danger"
                        isDisabled={selectedCount === 0}
                        onPress={handleBatchDeleteClick}
                      >
                        <Trash2 className="size-4" stroke={1.5} />
                        删除
                      </Button>
                      <Button onPress={() => setUploadModalOpen(true)}>
                        <Upload className="size-4" stroke={1.5} />
                        {selectedCategoryId != null && selectedCategoryName
                          ? `上传到${selectedCategoryName}`
                          : "上传"}
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="relative min-h-0 min-w-0 overflow-hidden">
              <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
                {isLoading ? (
                  <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                    加载中…
                  </div>
                ) : null}
                <DataGrid<MediaItem>
                  allowsColumnResize
                  aria-label="资产文件列表"
                  className="flex h-full min-h-0 min-w-0 flex-col"
                  columns={columnsWithRowHeader}
                  contentClassName="min-w-0"
                  data={items}
                  defaultSelectedKeys={undefined}
                  getRowId={getRowId}
                  renderEmptyState={() => (
                    <div className="text-muted-foreground py-8 text-center text-sm">暂无文件</div>
                  )}
                  scrollContainerClassName={cn(
                    "h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain",
                    "[&_[data-slot=table-resizable-container]]:overflow-visible",
                    "[&_[data-slot=table-content]]:!overflow-visible",
                    "[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20",
                    "[&_th]:min-w-0 [&_td]:min-w-0",
                    "[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0"
                  )}
                  selectedKeys={selectedKeys}
                  selectionMode="multiple"
                  showSelectionCheckboxes
                  sortDescriptor={sortDescriptor}
                  variant="primary"
                  onSelectionChange={setSelectedKeys}
                  onSortChange={handleSortChange}
                />
              </div>
            </div>

            <div className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm">
              <div className="flex w-fit shrink-0 items-center">
                <Pagination className="w-fit max-w-none" size="sm">
                  <Pagination.Content className="flex w-fit min-w-0">
                    <Pagination.Item>
                      <Pagination.Previous
                        isDisabled={safePage <= 1}
                        onPress={() =>
                          setPagination((p) => ({
                            ...p,
                            pageIndex: Math.max(0, p.pageIndex - 1),
                          }))
                        }
                      >
                        <Pagination.PreviousIcon />
                      </Pagination.Previous>
                    </Pagination.Item>
                    {paginationPages.map((p, i) =>
                      p === "ellipsis" ? (
                        <Pagination.Item key={`e-${i}`}>
                          <Pagination.Ellipsis />
                        </Pagination.Item>
                      ) : (
                        <Pagination.Item key={p}>
                          <Pagination.Link
                            isActive={p === safePage}
                            onPress={() =>
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: (p as number) - 1,
                              }))
                            }
                          >
                            {p}
                          </Pagination.Link>
                        </Pagination.Item>
                      )
                    )}
                    <Pagination.Item>
                      <Pagination.Next
                        isDisabled={safePage >= pageCount}
                        onPress={() =>
                          setPagination((p) => ({
                            ...p,
                            pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                          }))
                        }
                      >
                        <Pagination.NextIcon />
                      </Pagination.Next>
                    </Pagination.Item>
                  </Pagination.Content>
                </Pagination>
              </div>

              <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
                <InlineSelect
                  aria-label="每页条数"
                  value={String(pagination.pageSize)}
                  onChange={(v) => {
                    if (v) {
                      setPagination((p) => ({
                        ...p,
                        pageSize: Number(v),
                        pageIndex: 0,
                      }));
                    }
                  }}
                >
                  <InlineSelect.Trigger>
                    <span className="text-muted">每页</span>
                    <InlineSelect.Value />
                    <InlineSelect.Indicator />
                  </InlineSelect.Trigger>
                  <InlineSelect.Popover className="w-[88px]">
                    <ListBox>
                      {pageSizes.map((n) => (
                        <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                          {n}
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </InlineSelect.Popover>
                </InlineSelect>
                <Separator className="!h-4 shrink-0" orientation="vertical" />
                <span className="text-muted shrink-0">
                  第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                  {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog
        isOpen={isBatchDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsBatchDeleteDialogOpen(open);
          if (!open) setSelectedKeys(new Set());
        }}
      >
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除已选中的 {selectedCount} 个文件吗？删除后不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setIsBatchDeleteDialogOpen(false);
                    setSelectedKeys(new Set());
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMediaMutation.isPending}
                  onPress={() => {
                    void handleConfirmBatchDelete();
                  }}
                >
                  {deleteMediaMutation.isPending ? "删除中…" : "确认删除"}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <AssetsPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} media={previewMedia} />

      <UploadFilesModal
        isOpen={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        title={
          selectedCategoryId != null && selectedCategoryName
            ? `上传到「${selectedCategoryName}」`
            : "上传文件"
        }
        uploadOptions={{
          categoryId: selectedCategoryId ?? undefined,
          isSharedToSpace: scope === "shared",
          isOwner: scope === "my",
        }}
      />
    </>
  );
}
